import { useContext, useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Context from '../../context';
import './List.scss';

const List = ({ date, type, data, isCarouselSlide, slideNumIdx, slideNum, passItemsDataToDailyActivities, setDocumentViewerData }) => {
  const [listContent, setListContent] = useState(null);
  const [doInterval, setDoInterval] = useState(false);
  const { viewMode, itin, icons } = useContext(Context);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  const generateDateStrings = useCallback(() => {
    if (!date) return null;
    return {
      dayName: date.toLocaleDateString(['en-GB'], { weekday: 'long' }),
      dayNameLocalised: date.toLocaleDateString([itin.language || 'en-GB'], { weekday: 'long' }),
      dateStringLocalised: date.toLocaleDateString([itin.language || 'en-GB'], {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
    };
  }, [date, itin?.language]);

  const renderDateInfo = () => {
    if (!isCarouselSlide || !date) return null;
    const { dayNameLocalised, dateStringLocalised } = generateDateStrings();
    return (
      <div className="date-info">
        <span className="day-name">{dayNameLocalised}</span>
        <span className="date-string">{dateStringLocalised}</span>
      </div>
    );
  };

  const renderTitle = () => {
    if (!data.title || viewMode !== 'desktop') return null;
    return (
      <div className="title">{data.title}</div>
    );
  };

  const renderDescription = useCallback(() => {
    if (!data.description) return null;
    return (
      <div className="description">{data.description}</div>
    );
  }, [data.description]);

  const renderVideo = () => {
    const video = data?.videoNode?.file?.httpsUrl;
    if (!video) return null;
    return (
      <div className="video-container">
        <video
          controls
          preload="metadata"
        >
          <source src={video} />
          <p>
            Your browser doesn't support HTML5 video. Here is a <a href={video}>link to the video</a> instead.
          </p>
        </video>
      </div>
    );
  };

  const renderButtons = useCallback(() => {
    if (!data?.menu || !data?.menu?.length || type !== "directories") return null;
    const handleClick = (doc, event) => {
      event.preventDefault();
      setDocumentViewerData(doc);
      navigate(`${location.pathname}/view/${doc.id}`);
    };

    const expandedMenu = data.menu.map(item => {
      const doc = itin.documents.find(doc => doc.id === item.segueId);
      return {
        ...item,
        segueNode: doc || null
      };
    });

    return (
      <ul className="buttons">
        {
          expandedMenu.map((x, i) => (
            <li key={i}>
              {
                (x.segueNode?.type === "file" || x.segueNode?.type === "bundle") ? (
                  <button
                    onClick={(e) => handleClick(x.segueNode, e)}
                  >
                    <img src={icons[x.iconId]} alt='' />
                    {x.label}
                  </button>
                ) : (
                  <a
                    href={x.segueNode.remoteUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={icons[x.iconId]} alt='' />
                    <span>{x.label}</span>
                  </a>
                )
              }
            </li>
          ))
        }
      </ul>
    );
  }, [data.menu, icons, itin.documents, location.pathname, navigate, setDocumentViewerData, type]);

  useEffect(() => {
    const renderList = (data, isDailyActivities) => {
      if (!isDailyActivities && (!data || data.length === 0)) {
        setDoInterval(false);
        setListContent(null);
        return null;
      }
      const dateInfo = generateDateStrings();
      const nowMs = new Date().getTime();
      let doRunInterval = false;
      const tmp = [];
      for (const item of data) {
        if (date && item.weekdaysRestriction && !item.weekdaysRestriction.includes(dateInfo.dayName.toLowerCase())) continue;

        const startMs = item.restrictionStartDate ? item.restrictionStartDate * 1000 : null;
        const endMs = item.restrictionEndDate ? item.restrictionEndDate * 1000 : null;

        if (startMs && nowMs < startMs) {
          doRunInterval = true;
          continue;
        }

        if (endMs) {
          if (nowMs < endMs) doRunInterval = true;
          if (nowMs >= endMs) continue;
        }

        tmp.push(item);
      }
      data = tmp;

      setDoInterval(doRunInterval);
      if (type === 'dailyActivities' && isDailyActivities && passItemsDataToDailyActivities) passItemsDataToDailyActivities(slideNumIdx - 1, tmp);
      setListContent(
        <div className={`list-items-container list-items-container-${type}`}>
          { renderDescription() }
          {data.length > 0 ? (
            data.map((x, i) => {
              const bgUrl = itin.documents.find(doc => doc.id === x.backgroundNodeId)?.file?.httpsUrl ?? itin?.photoNode?.file?.httpsUrl;
              return (
                <Link
                  className={`list-item list-item-${type} tile-grid-tile`}
                  key={i}
                  to={`${pathname}${pathname.endsWith('/') ? '' : '/'}${x.id}`}
                  style={{ backgroundImage: (type !== 'directories' && bgUrl) ? `url("${bgUrl}")` : null }}
                  tabIndex={(slideNumIdx === slideNum)
                    ? 0
                    : -1
                  }
                >
                  <div className="list-item-gradient">
                    <div className="title">{x.title}</div>
                    {/*<div className="title">{x.title} {x.title} {x.title}</div>*/}
                    {
                      type !== 'directories' &&
                      <div className="description">{x.description}</div>
                      // <div className="description">{x.description} {x.description}</div>
                    }
                    {
                      x.entryType === 'list' &&
                      <img className="chevron" src="/icons/chevron-down.png" alt="" />
                    }
                  </div>
                </Link>
              )
            })
          ) : (
            <Link
              className={`list-item list-item-placeholder`}
              to={pathname}
              aria-disabled="true"
              tabIndex="-1"
            >
            </Link>
          )
          }
        </div>
      );
    }

    const listData = data?.entryType === 'list' ? data.children : data;
    renderList(listData, !!itin?.dailyActivities?.find(item => item?.id === data[0]?.id));
    let intervalId;
    if (doInterval) {
      intervalId = setInterval(renderList, 1000 * 60 * 30, listData);
    }
    //to move focus in multilevel lists to the first element
    document.querySelector(".list-item")?.focus();


    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [itin?.dailyActivities, passItemsDataToDailyActivities, data, date, doInterval, generateDateStrings, pathname, slideNum, slideNumIdx, type, itin.documents, itin?.photoNode?.file?.httpsUrl, renderDescription]);

  return (
    <div
      className={`list list-${type}`}
    >
      { renderDateInfo() }
      { renderTitle() }
      { renderVideo() }
      { listContent }
      { renderButtons() }
    </div>
  );
};

export default List;
